/**
 * Theme management composable
 * Handles light/dark theme switching with system preference detection
 */
export const useTheme = () => {
  const colorMode = useColorMode()

  // Available theme options
  const themes = [
    { value: 'light', label: 'Light', icon: 'i-heroicons-sun' },
    { value: 'dark', label: 'Dark', icon: 'i-heroicons-moon' },
    { value: 'system', label: 'System', icon: 'i-heroicons-computer-desktop' }
  ]

  // Current theme — use preference so 'system' shows as active
  const currentTheme = computed(() => colorMode.preference)

  // Is dark mode active
  const isDark = computed(() => colorMode.value === 'dark')

  // Apply theme class directly to DOM (same logic as useSettings.applyTheme)
  const applyThemeToDom = (theme: string) => {
    if (typeof window === 'undefined') return
    const html = document.documentElement
    html.classList.remove('dark', 'light')
    if (theme === 'dark') {
      html.classList.add('dark')
    } else if (theme === 'system') {
      if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
        html.classList.add('dark')
      }
    }
    // Persist to userSettings in localStorage so useSettings stays in sync
    try {
      const raw = localStorage.getItem('userSettings')
      const saved = raw ? JSON.parse(raw) : {}
      saved.theme = theme
      localStorage.setItem('userSettings', JSON.stringify(saved))
    } catch {}
  }

  // Set theme
  const setTheme = (theme: string) => {
    colorMode.preference = theme
    applyThemeToDom(theme)
  }

  // Toggle between light and dark (skip system)
  const toggleTheme = () => {
    if (colorMode.value === 'light') {
      setTheme('dark')
    } else {
      setTheme('light')
    }
  }

  // Get current theme info
  const currentThemeInfo = computed(() => {
    return themes.find(theme => theme.value === colorMode.preference) || themes[0]
  })

  return {
    themes,
    currentTheme,
    isDark,
    setTheme,
    toggleTheme,
    currentThemeInfo
  }
}
